import classNames from "classnames";
import * as React from "react";

import styles from "./Table.module.css";

/** Which edge a column sticks to while its row scrolls sideways. */
export type TableStickySide = "left" | "right";

export type TableProps = React.ComponentPropsWithoutRef<"table"> & {
  /**
   * `fixed` sizes columns from the first row (or a `colgroup`) rather than
   * from their contents, so long text wraps instead of widening its column.
   */
  layout?: "auto" | "fixed";
};

export type TableHeaderProps = React.ComponentPropsWithoutRef<"thead"> & {
  /** `compact` tightens the header row. */
  variant?: "default" | "compact";
  /** Keeps the header row at the top of a scrolling parent. */
  sticky?: boolean;
};

export type TableHeadProps = React.ComponentPropsWithoutRef<"th"> & {
  sticky?: TableStickySide;
};

export type TableBodyProps = React.ComponentPropsWithoutRef<"tbody">;

export type TableRowProps = React.ComponentPropsWithoutRef<"tr"> & {
  variant?: "default" | "selected";
};

export type TableCellProps = React.ComponentPropsWithoutRef<"td"> & {
  sticky?: TableStickySide;
};

export type TableFooterProps = React.ComponentPropsWithoutRef<"tfoot">;

const TableRoot = React.forwardRef<HTMLTableElement, TableProps>(function Table(
  { layout = "auto", className, ...props },
  ref,
) {
  return (
    <table
      ref={ref}
      className={classNames(styles.table, className)}
      data-layout={layout}
      {...props}
    />
  );
});

/** The column headings, set apart from the rows under them. */
const Header = React.forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  function TableHeader(
    { variant = "default", sticky = false, className, ...props },
    ref,
  ) {
    return (
      <thead
        ref={ref}
        className={classNames(styles.header, className)}
        data-variant={variant}
        data-sticky={sticky || undefined}
        {...props}
      />
    );
  },
);

/**
 * A heading cell: a column's in `Table.Header`, or a row's in `Table.Body`
 * with `scope="row"`. Only the first takes the header's treatment.
 */
const Head = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  function TableHead({ sticky, className, ...props }, ref) {
    return (
      <th
        ref={ref}
        className={classNames(styles.head, className)}
        data-sticky={sticky}
        {...props}
      />
    );
  },
);

const Body = React.forwardRef<HTMLTableSectionElement, TableBodyProps>(
  function TableBody({ className, ...props }, ref) {
    return (
      <tbody
        ref={ref}
        className={classNames(styles.body, className)}
        {...props}
      />
    );
  },
);

const Row = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  function TableRow({ variant = "default", className, ...props }, ref) {
    return (
      <tr
        ref={ref}
        className={classNames(styles.row, className)}
        data-variant={variant}
        {...props}
      />
    );
  },
);

const Cell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  function TableCell({ sticky, className, ...props }, ref) {
    return (
      <td
        ref={ref}
        className={classNames(styles.cell, className)}
        data-sticky={sticky}
        {...props}
      />
    );
  },
);

/** Rows under the body that sum it up or lead on from it. */
const Footer = React.forwardRef<HTMLTableSectionElement, TableFooterProps>(
  function TableFooter({ className, ...props }, ref) {
    return (
      <tfoot
        ref={ref}
        className={classNames(styles.footer, className)}
        {...props}
      />
    );
  },
);

/**
 * A semantic table for rows you already have: it lays them out and never
 * fetches, sorts, filters, or pages (that is `DataTable`). Rows go in as
 * children; a loading or empty row is the consumer's own `Table.Row`. It is
 * its own box: a bordered, rounded table on one background.
 *
 * @example
 * ```tsx
 * <Table>
 *   <Table.Header>
 *     <Table.Row>
 *       <Table.Head>Name</Table.Head>
 *       <Table.Head>Status</Table.Head>
 *     </Table.Row>
 *   </Table.Header>
 *   <Table.Body>
 *     <Table.Row>
 *       <Table.Cell>Drive triage</Table.Cell>
 *       <Table.Cell>Working</Table.Cell>
 *     </Table.Row>
 *   </Table.Body>
 * </Table>
 * ```
 */
export const Table = Object.assign(TableRoot, {
  Header,
  Head,
  Body,
  Row,
  Cell,
  Footer,
});
